import { isMagicbuyProject } from './projectUtils'

export const DAMAGE_INSURANCE_RATE = 0.05
export const DAMAGE_INSURANCE_MIN_CLAIM_AMOUNT = 500
export const DAMAGE_INSURANCE_MIN_PREMIUM = 25
export const DAMAGE_INSURANCE_SEA_MIN_CLAIM_AMOUNT = 2000
export const DAMAGE_INSURANCE_SEA_MIN_PREMIUM = 200
export const DAMAGE_INSURANCE_MAX_CLAIM_AMOUNT = 1000000
export const DAMAGE_INSURANCE_AGREEMENT_POST_ID = '652998'
export const MGB_DAMAGE_INSURANCE_AGREEMENT_POST_ID = '1636264'
export const DAMAGE_INSURANCE_EXCLUDED_WAREHOUSE_ID = '1598'
export const DAMAGE_INSURANCE_BRIEF_OPTIONS = Object.freeze([
  '問題件拿出，其他件寄回',
  '沒關係，壞了也寄回',
])
export const DEFAULT_DAMAGE_INSURANCE_BRIEF = DAMAGE_INSURANCE_BRIEF_OPTIONS[0]
export const ORDER_CREATE_DAMAGE_INSURANCE_ENABLED = true

export function getDamageInsuranceAgreementPostId (project) {
  return isMagicbuyProject(project)
    ? MGB_DAMAGE_INSURANCE_AGREEMENT_POST_ID
    : DAMAGE_INSURANCE_AGREEMENT_POST_ID
}

export function getDamageInsuranceAgreementTitle (project) {
  return `${isMagicbuyProject(project) ? '買即可' : '巧巧郎'}運損保價協議`
}

function parcelExtraInfo (parcel) {
  return parcel?.extraInfo || {}
}

export function getDamageInsurancePricingPolicy (transport) {
  if (transport === 'sea') {
    return {
      minClaimAmount: DAMAGE_INSURANCE_SEA_MIN_CLAIM_AMOUNT,
      minPremium: DAMAGE_INSURANCE_SEA_MIN_PREMIUM,
      rate: DAMAGE_INSURANCE_RATE,
    }
  }
  return {
    minClaimAmount: DAMAGE_INSURANCE_MIN_CLAIM_AMOUNT,
    minPremium: DAMAGE_INSURANCE_MIN_PREMIUM,
    rate: DAMAGE_INSURANCE_RATE,
  }
}

export function normalizeDamageInsuranceClaimAmount (value, transport) {
  const { minClaimAmount } = getDamageInsurancePricingPolicy(transport)
  const amount = Number(value)
  if (!Number.isFinite(amount) || amount < minClaimAmount) {
    return minClaimAmount
  }
  return Math.min(Math.floor(amount), DAMAGE_INSURANCE_MAX_CLAIM_AMOUNT)
}

export function calculateDamageInsurancePremium (claimAmount, transport) {
  const amount = Number(claimAmount)
  if (!Number.isFinite(amount) || amount <= 0) return 0
  const { minPremium, rate } = getDamageInsurancePricingPolicy(transport)
  return Math.max(minPremium, Math.ceil(amount * rate))
}

export function normalizeDamageInsuranceBrief (value) {
  return DAMAGE_INSURANCE_BRIEF_OPTIONS.includes(value)
    ? value
    : DEFAULT_DAMAGE_INSURANCE_BRIEF
}

// brand-fence:start id=kkren-order-damage-insurance-warehouses brand=kkren predicate=data-driven
export function isDamageInsuranceWarehouseParcel (parcel) {
  const warehouseId = String(parcel?.jyAddrid || '')
  return Boolean(warehouseId) && warehouseId !== DAMAGE_INSURANCE_EXCLUDED_WAREHOUSE_ID
}

export function isOrderCreateDamageInsuranceEnabled ({
  project,
  parcels,
} = {}) {
  return ORDER_CREATE_DAMAGE_INSURANCE_ENABLED &&
    !isMagicbuyProject(project) &&
    Array.isArray(parcels) &&
    parcels.some(isDamageInsuranceWarehouseParcel)
}
// brand-fence:end id=kkren-order-damage-insurance-warehouses

export function hasDamageInsurance (parcel) {
  return Number(parcelExtraInfo(parcel).vaTDIPremium || 0) > 0
}

export function hasClosedDamageInsurance (parcel) {
  const extraInfo = parcelExtraInfo(parcel)
  return !hasDamageInsurance(parcel) &&
    Array.isArray(extraInfo.snapshotsTDIPremium) &&
    extraInfo.snapshotsTDIPremium.length > 0
}

export function canPurchaseDamageInsurance (parcel) {
  if (
    !isDamageInsuranceWarehouseParcel(parcel) ||
    hasDamageInsurance(parcel) ||
    hasClosedDamageInsurance(parcel)
  ) {
    return false
  }
  const extraInfo = parcelExtraInfo(parcel)
  const isStorage = typeof parcel.isStorage === 'boolean'
    ? parcel.isStorage
    : Boolean(parcel.jyStorageAt && parcel.jyStorageAt !== '0000-00-00 00:00:00')
  if (!isStorage || parcel.status === 'packed') return false
  if (Number(extraInfo.hasLgFragile) === 1) return false
  if (parcel.hasWooden && !(extraInfo.snapshotsTDIPremium || []).length) return false
  if (String(parcel.splitpri || '0') !== '0') return false
  return Number(parcel.weight || 0) <= 60000
}

export function getOrderCreateDamageInsuranceParcels (parcels) {
  if (!Array.isArray(parcels)) return []
  return parcels.filter(parcel => {
    return isDamageInsuranceWarehouseParcel(parcel) &&
      (hasDamageInsurance(parcel) || canPurchaseDamageInsurance(parcel))
  })
}

export function hasOrderCreateDamageInsuranceCoverage (parcels, selections = {}) {
  return getOrderCreateDamageInsuranceParcels(parcels).some(parcel => {
    return hasDamageInsurance(parcel) || Boolean(selections[String(parcel.pclid)])
  })
}

export function requiresDamageInsuranceRiskAcceptance (parcels, selections = {}) {
  if (!Array.isArray(parcels)) return false
  return parcels.some(parcel => {
    return canPurchaseDamageInsurance(parcel) && !selections[String(parcel.pclid)]
  })
}

export function hasSelectedDamageInsurancePurchases (parcels, selections = {}) {
  if (!Array.isArray(parcels)) return false
  return parcels.some(parcel => {
    return canPurchaseDamageInsurance(parcel) && Boolean(selections[String(parcel.pclid)])
  })
}

export function totalSelectedDamageInsurancePremium (parcels, selections = {}, transport) {
  if (!Array.isArray(parcels)) return 0
  return parcels.reduce((total, parcel) => {
    const selection = selections[String(parcel.pclid)]
    if (!selection || !canPurchaseDamageInsurance(parcel)) return total
    const claimAmount = normalizeDamageInsuranceClaimAmount(selection.claimAmount, transport)
    return total + calculateDamageInsurancePremium(claimAmount, transport)
  }, 0)
}

export function createDamageInsuranceSelection (parcel, claimAmount, transport) {
  const normalizedClaimAmount = normalizeDamageInsuranceClaimAmount(claimAmount, transport)
  return {
    pclid: parcel.pclid,
    claimAmount: normalizedClaimAmount,
    premium: calculateDamageInsurancePremium(normalizedClaimAmount, transport),
  }
}
